import React, { useEffect, useState, useRef } from 'react';
import { useAuth } from '../components/AuthContext ';
import axiosInstance from '../components/axiosInstance';
import { Navigate } from 'react-router-dom';
import { 
  faChartLine, faBlog, faUsers, faComments, faGamepad, 
  faEnvelope, faCog, faRobot, faExclamationTriangle,
  faCheckCircle, faTimesCircle, faEdit, faTrash, faEye, faBars, faTimes,
  faComment, faReply 
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const ComprehensiveAdminDashboard = () => {
  const { isAdmin, loading } = useAuth();
  const [activeSection, setActiveSection] = useState('dashboard');
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [pendingBlogs, setPendingBlogs] = useState([]);
  const [allBlogs, setAllBlogs] = useState([]);
  const [reports, setReports] = useState([]);
  const [selectedBlog, setSelectedBlog] = useState(null);
  const [editingBlog, setEditingBlog] = useState(null);
  const [bulkEmail, setBulkEmail] = useState({ subject: '', message: '', recipient_type: 'all', custom_emails: '' });
  const [activityContent, setActivityContent] = useState({ movies: [], games: [], exercises: [], sounds: [], books: [] });
  const [newUser, setNewUser] = useState({ username: '', email: '', password: '', first_name: '', last_name: '' });
  const [testMessage, setTestMessage] = useState('');
  const [aiResponse, setAiResponse] = useState('');
  const [activityForm, setActivityForm] = useState(null);
  const [activityType, setActivityType] = useState(null);
  const [communityMessages, setCommunityMessages] = useState([]);
  const [newBlogImage, setNewBlogImage] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  
  const blogEditorRef = useRef(null);
  const bulkEmailEditorRef = useRef(null);

  useEffect(() => {
    if (!isAdmin) return;
    fetchStats();
  }, [isAdmin]);

  useEffect(() => {
    if (!isAdmin) return;
    switch (activeSection) {
      case 'blogs':
        fetchBlogs();
        break;
      case 'users':
        fetchUsers();
        break;
      case 'reports':
        fetchReports();
        break;
      case 'activities':
        fetchActivities();
        break;
      case 'community':
        fetchCommunityMessages();
        break;
      default:
        break;
    }
  }, [activeSection, isAdmin]);

  // Set initial content for blog editor when editing starts
  useEffect(() => {
    if (editingBlog && blogEditorRef.current) {
      blogEditorRef.current.innerHTML = editingBlog.content || '';
    }
  }, [editingBlog?.id]); // Only re-run when we start editing a different blog

  // Set initial content for bulk email editor
  useEffect(() => {
    if (bulkEmail.message && bulkEmailEditorRef.current && !bulkEmailEditorRef.current.contains(document.activeElement)) {
      bulkEmailEditorRef.current.innerHTML = bulkEmail.message;
    }
  }, [bulkEmail.message]);

  const fetchStats = async () => {
    try {
      const res = await axiosInstance.get('admin/stats/');
      setStats(res.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await axiosInstance.get('admin/users/');
      setUsers(res.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchBlogs = async () => {
    try {
      const [pendingRes, allRes] = await Promise.all([
        axiosInstance.get('admin/blog-posts/', { params: { status: 'pending' } }),
        axiosInstance.get('admin/blog-posts/'),
      ]);
      setPendingBlogs(pendingRes.data);
      setAllBlogs(allRes.data);
    } catch (error) {
      console.error('Error fetching blogs:', error);
    }
  };

  const fetchReports = async () => {
    try {
      const res = await axiosInstance.get('admin/reports/');
      setReports(res.data);
    } catch (error) {
      console.error('Error fetching reports:', error);
    }
  };

  const fetchActivities = async () => {
    try {
      const [movies, games, exercises, sounds, books] = await Promise.all([
        axiosInstance.get('admin/activity/movies/'),
        axiosInstance.get('admin/activity/games/'),
        axiosInstance.get('admin/activity/exercises/'),
        axiosInstance.get('admin/activity/sounds/'),
        axiosInstance.get('admin/activity/books/'),
      ]);
      setActivityContent({
        movies: movies.data,
        games: games.data,
        exercises: exercises.data,
        sounds: sounds.data,
        books: books.data,
      });
    } catch (error) {
      console.error('Error fetching activities:', error);
    }
  };

  const fetchCommunityMessages = async () => {
    try {
      const res = await axiosInstance.get('admin/community-messages/');
      setCommunityMessages(res.data);
    } catch (error) {
      console.error('Error fetching community messages:', error);
    }
  };

  const handleApproveBlog = async (id) => {
    try {
      await axiosInstance.patch(`admin/blog-posts/${id}/`, { approved: true });
      fetchBlogs();
      setSelectedBlog(null);
      alert('Blog approved successfully!');
    } catch (error) {
      console.error('Error approving blog:', error);
      alert('Failed to approve blog');
    }
  };

  const handleRejectBlog = async (id) => {
    const reason = prompt('Enter rejection reason:');
    if (!reason) return;
    try {
      await axiosInstance.post(`admin/blog-posts/${id}/reject/`, { reason });
      fetchBlogs();
      setSelectedBlog(null);
      alert('Blog rejected and author notified');
    } catch (error) {
      console.error('Error rejecting blog:', error);
      alert('Failed to reject blog');
    }
  };

  const handleUpdateBlog = async () => {
    if (!editingBlog) return;
    try {
      const formData = new FormData();
      formData.append('title', editingBlog.title);
      formData.append('content', editingBlog.content);
      formData.append('fullname', editingBlog.fullname);
      formData.append('email', editingBlog.email);
      formData.append('approved', editingBlog.approved);
      
      // Add social media fields
      if (editingBlog.twitter) formData.append('twitter', editingBlog.twitter);
      if (editingBlog.facebook) formData.append('facebook', editingBlog.facebook);
      if (editingBlog.instagram) formData.append('instagram', editingBlog.instagram);
      if (editingBlog.linkedin) formData.append('linkedin', editingBlog.linkedin);
      if (editingBlog.website) formData.append('website', editingBlog.website);
      
      // Add new image if uploaded
      if (newBlogImage) {
        formData.append('image', newBlogImage);
      }
      
      await axiosInstance.patch(`admin/blog-posts/${editingBlog.id}/`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      fetchBlogs();
      setEditingBlog(null);
      setNewBlogImage(null);
      alert('Blog updated successfully');
    } catch (error) {
      console.error('Error updating blog:', error);
      alert('Failed to update blog');
    }
  };

  const handleDeleteBlog = async (id) => {
    if (!window.confirm('Delete this blog permanently?')) return;
    try {
      await axiosInstance.delete(`admin/blog-posts/${id}/`);
      fetchBlogs();
      setSelectedBlog(null);
      alert('Blog deleted');
    } catch (error) {
      console.error('Error deleting blog:', error);
      alert('Failed to delete blog');
    }
  };

  const handleCreateUser = async () => {
    try {
      await axiosInstance.post('admin/users/', newUser);
      fetchUsers();
      setNewUser({ username: '', email: '', password: '', first_name: '', last_name: '' });
      alert('User created successfully');
    } catch (error) {
      console.error('Error creating user:', error);
      alert('Failed to create user');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Delete this user?')) return;
    try {
      await axiosInstance.delete(`admin/users/${userId}/`);
      fetchUsers();
      alert('User deleted');
    } catch (error) {
      console.error('Error deleting user:', error);
      alert('Failed to delete user');
    }
  };

  const handleToggleUserStaff = async (userId, currentStatus) => {
    try {
      await axiosInstance.patch(`admin/users/${userId}/`, { is_staff: !currentStatus });
      fetchUsers();
    } catch (error) {
      console.error('Error updating user:', error);
      alert('Failed to update user');
    }
  };

  const handleDeleteMessage = async (messageId) => {
    if (!window.confirm('Delete this message?')) return;
    try {
      await axiosInstance.delete(`message/${messageId}/delete/`);
      fetchReports();
      fetchCommunityMessages();
      alert('Message deleted');
    } catch (error) {
      console.error('Error deleting message:', error);
      alert('Failed to delete message');
    }
  };

  const handleSendBulkEmail = async () => {
    const recipientLabel = {
      all: 'all users',
      staff: 'staff only',
      newsletter: 'newsletter subscribers',
      custom: 'custom recipients'
    }[bulkEmail.recipient_type] || bulkEmail.recipient_type;
    
    if (!window.confirm(`Send email to ${recipientLabel}?\n\nEmails will be sent in batches with delays to prevent spam detection.`)) return;
    
    if (bulkEmail.recipient_type === 'custom' && !bulkEmail.custom_emails.trim()) {
      alert('Please enter at least one email address');
      return;
    }
    
    // Show loading message
    const loadingAlert = document.createElement('div');
    loadingAlert.style.cssText = 'position: fixed; top: 20px; right: 20px; background: #4CAF50; color: white; padding: 15px 20px; border-radius: 5px; z-index: 9999; box-shadow: 0 2px 10px rgba(0,0,0,0.2);';
    loadingAlert.textContent = '📧 Sending emails in batches...';
    document.body.appendChild(loadingAlert);
    
    try {
      const res = await axiosInstance.post('admin/bulk-email/', bulkEmail);
      
      // Remove loading alert
      document.body.removeChild(loadingAlert);
      
      // Show detailed results
      let resultMessage = `✅ ${res.data.message}\\n\\n`;
      resultMessage += `📊 Summary:\\n`;
      resultMessage += `   • Total: ${res.data.total} recipients\\n`;
      resultMessage += `   • Sent: ${res.data.sent} ✓\\n`;
      if (res.data.failed > 0) {
        resultMessage += `   • Failed: ${res.data.failed} ✗\\n`;
      }
      resultMessage += `   • Batches: ${res.data.batches}\\n`;
      
      if (res.data.errors && res.data.errors.length > 0) {
        resultMessage += `\\n⚠️ Errors:\\n`;
        res.data.errors.forEach(err => {
          resultMessage += `   • ${err}\\n`;
        });
      }
      
      alert(resultMessage);
      
      // Clear form
      setBulkEmail({ subject: '', message: '', recipient_type: 'all', custom_emails: '' });
      if (bulkEmailEditorRef.current) {
        bulkEmailEditorRef.current.innerHTML = '';
      }
    } catch (error) {
      // Remove loading alert if present
      if (document.body.contains(loadingAlert)) {
        document.body.removeChild(loadingAlert);
      }
      
      console.error('Error sending bulk email:', error);
      
      const errorData = error.response?.data;
      let errorMessage = '❌ Failed to send emails\\n\\n';
      
      if (errorData?.error) {
        errorMessage += `Error: ${errorData.error}\\n`;
        
        // Show partial success if any
        if (errorData.sent > 0) {
          errorMessage += `\\n✓ Successfully sent: ${errorData.sent}\\n`;
          errorMessage += `✗ Failed: ${errorData.failed}\\n`;
        }
        
        // Show error details
        if (errorData.details && errorData.details.length > 0) {
          errorMessage += `\\nDetails:\\n`;
          errorData.details.forEach(detail => {
            errorMessage += `• ${detail}\\n`;
          });
        }
        
        errorMessage += `\\n💡 Troubleshooting:\\n`;
        errorMessage += `• Check your internet connection\\n`;
        errorMessage += `• Verify SMTP server settings\\n`;
        errorMessage += `• Contact system administrator if issue persists`;
      } else {
        errorMessage += error.message || 'Unknown error occurred';
      }
      
      alert(errorMessage);
    }
  };

  const handleTestAI = async () => {
    try {
      const res = await axiosInstance.post('chat/enhanced/', { message: testMessage });
      setAiResponse(res.data.response);
    } catch (error) {
      console.error('Error testing AI:', error);
      setAiResponse('Error: ' + error.message);
    }
  };

  const handleDeleteActivity = async (type, id) => {
    if (!window.confirm(`Delete this ${type}?`)) return;
    try {
      await axiosInstance.delete(`admin/activity/${type}/${id}/`);
      fetchActivities();
      alert(`${type} deleted`);
    } catch (error) {
      console.error(`Error deleting ${type}:`, error);
      alert(`Failed to delete ${type}`);
    }
  };

  const handleCreateActivity = (type) => {
    const emptyForms = {
      movies: { title: '', description: '', image_url: '', external_url: '', moods: '' },
      games: { title: '', description: '', image_url: '', play_url: '', moods: '' },
      exercises: { name: '', exercise_type: '', reason: '', gif_url: '', moods: '' },
      sounds: { name: '', category: '', duration: '', audio_url: '', image_url: '', moods: '' },
      books: { title: '', author: '', description: '', cover_image_url: '', purchase_url: '', moods: '' },
    };
    setActivityType(type);
    setActivityForm({ ...emptyForms[type], isNew: true });
  };

  const handleEditActivity = (type, item) => {
    setActivityType(type);
    setActivityForm({ ...item, isNew: false });
  };

  const handleSaveActivity = async () => {
    if (!activityForm || !activityType) return;
    try {
      if (activityForm.isNew) {
        const { isNew, ...data } = activityForm;
        await axiosInstance.post(`admin/activity/${activityType}/`, data);
        alert(`${activityType} created successfully`);
      } else {
        const { isNew, ...data } = activityForm;
        await axiosInstance.put(`admin/activity/${activityType}/${activityForm.id}/`, data);
        alert(`${activityType} updated successfully`);
      }
      fetchActivities();
      setActivityForm(null);
      setActivityType(null);
    } catch (error) {
      console.error('Error saving activity:', error);
      alert('Failed to save activity');
    }
  };

  const handleCancelActivityForm = () => {
    setActivityForm(null);
    setActivityType(null);
  };

  if (loading) {
    return <div className="p-8">Loading...</div>;
  }

  if (!isAdmin) {
    return <Navigate to="/dashboardmain" replace />;
  }

  return (
    <div className="flex min-h-screen bg-gray-50">
      {/* Hamburger Menu Button for Small Screens */}
      <button
        onClick={() => setIsSidebarOpen(!isSidebarOpen)}
        className="fixed top-4 left-4 z-30 md:hidden bg-[#59844F] text-white p-3 rounded-lg shadow-lg"
      >
        <FontAwesomeIcon icon={isSidebarOpen ? faTimes : faBars} size="lg" />
      </button>

      {/* Sidebar */}
      <div className={`transform ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0 w-64 bg-[#59844F] text-white min-h-screen fixed left-0 top-0 overflow-y-auto transition-transform duration-300 ease-in-out z-20`}>
        <div className="p-6">
          <h1 className="text-2xl font-bold mb-8">Admin Panel</h1>
          <nav className="space-y-2">
            <button
              onClick={() => setActiveSection('dashboard')}
              className={`w-full text-left px-4 py-3 rounded flex items-center ${
                activeSection === 'dashboard' ? 'bg-white text-[#59844F]' : 'hover:bg-[#4b7443]'
              }`}
            >
              <FontAwesomeIcon icon={faChartLine} className="mr-3" />
              Dashboard
            </button>
            <button
              onClick={() => setActiveSection('blogs')}
              className={`w-full text-left px-4 py-3 rounded flex items-center ${
                activeSection === 'blogs' ? 'bg-white text-[#59844F]' : 'hover:bg-[#4b7443]'
              }`}
            >
              <FontAwesomeIcon icon={faBlog} className="mr-3" />
              Blog Management
            </button>
            <button
              onClick={() => setActiveSection('users')}
              className={`w-full text-left px-4 py-3 rounded flex items-center ${
                activeSection === 'users' ? 'bg-white text-[#59844F]' : 'hover:bg-[#4b7443]'
              }`}
            >
              <FontAwesomeIcon icon={faUsers} className="mr-3" />
              User Management
            </button>
            <button
              onClick={() => setActiveSection('reports')}
              className={`w-full text-left px-4 py-3 rounded flex items-center ${
                activeSection === 'reports' ? 'bg-white text-[#59844F]' : 'hover:bg-[#4b7443]'
              }`}
            >
              <FontAwesomeIcon icon={faComments} className="mr-3" />
              Reports
            </button>
            <button
              onClick={() => setActiveSection('community')}
              className={`w-full text-left px-4 py-3 rounded flex items-center ${
                activeSection === 'community' ? 'bg-white text-[#59844F]' : 'hover:bg-[#4b7443]'
              }`}
            >
              <FontAwesomeIcon icon={faUsers} className="mr-3" />
              Community Messages
            </button>
            <button
              onClick={() => setActiveSection('activities')}
              className={`w-full text-left px-4 py-3 rounded flex items-center ${
                activeSection === 'activities' ? 'bg-white text-[#59844F]' : 'hover:bg-[#4b7443]'
              }`}
            >
              <FontAwesomeIcon icon={faGamepad} className="mr-3" />
              Activity Lounge
            </button>
            <button
              onClick={() => setActiveSection('ai')}
              className={`w-full text-left px-4 py-3 rounded flex items-center ${
                activeSection === 'ai' ? 'bg-white text-[#59844F]' : 'hover:bg-[#4b7443]'
              }`}
            >
              <FontAwesomeIcon icon={faRobot} className="mr-3" />
              AI Testing
            </button>
            <button
              onClick={() => setActiveSection('email')}
              className={`w-full text-left px-4 py-3 rounded flex items-center ${
                activeSection === 'email' ? 'bg-white text-[#59844F]' : 'hover:bg-[#4b7443]'
              }`}
            >
              <FontAwesomeIcon icon={faEnvelope} className="mr-3" />
              Bulk Email
            </button>
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <div className="ml-64 flex-1 p-8">
        {/* Dashboard Section */}
        {activeSection === 'dashboard' && (
          <div>
            <h2 className="text-3xl font-bold mb-6">Dashboard Overview</h2>
            {!stats ? (
              <div className="text-center py-12">
                <p className="text-lg text-gray-600">Loading...</p>
              </div>
            ) : (
            <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold mb-4">Users</h3>
                <p className="text-3xl font-bold text-[#59844F]">{stats.users.total}</p>
                <p className="text-sm text-gray-600">Active: {stats.users.active}</p>
                <p className="text-sm text-gray-600">Staff: {stats.users.staff}</p>
              </div>
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold mb-4">Blogs</h3>
                <p className="text-3xl font-bold text-[#59844F]">{stats.blogs.total}</p>
                <p className="text-sm text-gray-600">Approved: {stats.blogs.approved}</p>
                <p className="text-sm text-yellow-600">Pending: {stats.blogs.pending}</p>
              </div>
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold mb-4">Community</h3>
                <p className="text-3xl font-bold text-[#59844F]">{stats.community.total_communities}</p>
                <p className="text-sm text-gray-600">Messages: {stats.community.total_messages}</p>
                <p className="text-sm text-red-600">Reports: {stats.community.total_reports}</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold mb-4">Content Stats</h3>
                <div className="space-y-2">
                  <p>Journals: {stats.content.journals}</p>
                  <p>Mood Entries: {stats.content.mood_entries}</p>
                  <p>AI Conversations: {stats.content.ai_conversations}</p>
                </div>
              </div>
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold mb-4">Activity Lounge</h3>
                <div className="space-y-2">
                  <p>Movies: {stats.content.movies}</p>
                  <p>Games: {stats.content.games}</p>
                  <p>Exercises: {stats.content.exercises}</p>
                  <p>Sounds: {stats.content.sounds}</p>
                </div>
              </div>
            </div>
            </>
            )}
          </div>
        )}

        {/* Blog Management Section */}
        {activeSection === 'blogs' && (
          <div>
            <h2 className="text-3xl font-bold mb-6">Blog Management</h2>
            
            {selectedBlog && !editingBlog && (
              <div className="bg-white rounded-lg shadow p-6 mb-6">
                <div className="flex justify-between items-start mb-4">
                  <h3 className="text-2xl font-bold">{selectedBlog.title}</h3>
                  <button onClick={() => setSelectedBlog(null)} className="text-gray-500 hover:text-gray-700">
                    <FontAwesomeIcon icon={faTimesCircle} size="lg" />
                  </button>
                </div>
                {selectedBlog.image && (
                  <img src={selectedBlog.image} alt={selectedBlog.title} className="w-full max-h-96 object-cover rounded mb-4" />
                )}
                <div className="mb-4">
                  <p className="text-sm text-gray-600">By: {selectedBlog.fullname} ({selectedBlog.email})</p>
                  <p className="text-sm text-gray-600">Created: {new Date(selectedBlog.created_at).toLocaleString()}</p>
                  <p className="text-sm">
                    Status: <span className={selectedBlog.approved ? 'text-green-600' : 'text-yellow-600'}>
                      {selectedBlog.approved ? 'Approved' : 'Pending'}
                    </span>
                  </p>
                  {(selectedBlog.twitter || selectedBlog.facebook || selectedBlog.instagram || selectedBlog.linkedin || selectedBlog.website) && (
                    <div className="mt-3">
                      <p className="text-sm font-semibold text-gray-700 mb-2">Author's Social Media:</p>
                      <div className="flex flex-wrap gap-2">
                        {selectedBlog.twitter && (
                          <a href={selectedBlog.twitter} target="_blank" rel="noopener noreferrer" className="px-3 py-1 bg-blue-400 text-white rounded text-xs hover:bg-blue-500">
                            Twitter/X
                          </a>
                        )}
                        {selectedBlog.facebook && (
                          <a href={selectedBlog.facebook} target="_blank" rel="noopener noreferrer" className="px-3 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700">
                            Facebook
                          </a>
                        )}
                        {selectedBlog.instagram && (
                          <a href={selectedBlog.instagram} target="_blank" rel="noopener noreferrer" className="px-3 py-1 bg-pink-600 text-white rounded text-xs hover:bg-pink-700">
                            Instagram
                          </a>
                        )}
                        {selectedBlog.linkedin && (
                          <a href={selectedBlog.linkedin} target="_blank" rel="noopener noreferrer" className="px-3 py-1 bg-blue-700 text-white rounded text-xs hover:bg-blue-800">
                            LinkedIn
                          </a>
                        )}
                        {selectedBlog.website && (
                          <a href={selectedBlog.website} target="_blank" rel="noopener noreferrer" className="px-3 py-1 bg-gray-600 text-white rounded text-xs hover:bg-gray-700">
                            Website
                          </a>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                <div className="prose max-w-none mb-6" dangerouslySetInnerHTML={{ __html: selectedBlog.content }} />
                <div className="flex space-x-3">
                  <button
                    onClick={() => setEditingBlog(selectedBlog)}
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                  >
                    <FontAwesomeIcon icon={faEdit} className="mr-2" />
                    Edit
                  </button>
                  {!selectedBlog.approved && (
                    <>
                      <button
                        onClick={() => handleApproveBlog(selectedBlog.id)}
                        className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                      >
                        <FontAwesomeIcon icon={faCheckCircle} className="mr-2" />
                        Approve
                      </button>
                      <button
                        onClick={() => handleRejectBlog(selectedBlog.id)}
                        className="px-4 py-2 bg-yellow-600 text-white rounded hover:bg-yellow-700"
                      >
                        <FontAwesomeIcon icon={faTimesCircle} className="mr-2" />
                        Reject
                      </button>
                    </>
                  )}
                  <button
                    onClick={() => handleDeleteBlog(selectedBlog.id)}
                    className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                  >
                    <FontAwesomeIcon icon={faTrash} className="mr-2" />
                    Delete
                  </button>
                </div>
              </div>
            )}

            {editingBlog && (
              <div className="bg-white rounded-lg shadow p-6 mb-6">
                <h3 className="text-2xl font-bold mb-4">Edit Blog</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block mb-2 font-semibold">Title</label>
                    <input
                      type="text"
                      value={editingBlog.title}
                      onChange={(e) => setEditingBlog({ ...editingBlog, title: e.target.value })}
                      className="w-full px-4 py-2 border rounded"
                      placeholder="Blog title"
                    />
                  </div>
                  <div>
                    <label className="block mb-2 font-semibold">Featured Image</label>
                    {editingBlog.image && !newBlogImage && (
                      <div className="mb-2">
                        <img src={editingBlog.image} alt="Current" className="max-w-xs rounded" />
                        <p className="text-sm text-gray-500 mt-1">Current image (upload a new one to replace)</p>
                      </div>
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => setNewBlogImage(e.target.files[0])}
                      className="w-full px-4 py-2 border rounded"
                    />
                    {newBlogImage && (
                      <div className="mt-2">
                        <img src={URL.createObjectURL(newBlogImage)} alt="Preview" className="max-w-xs rounded" />
                        <p className="text-sm text-green-600 mt-1">New image ready to upload</p>
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block mb-2 font-semibold">Author Name</label>
                    <input
                      type="text"
                      value={editingBlog.fullname}
                      onChange={(e) => setEditingBlog({ ...editingBlog, fullname: e.target.value })}
                      className="w-full px-4 py-2 border rounded"
                      placeholder="Author name"
                    />
                  </div>
                  <div>
                    <label className="block mb-2 font-semibold">Author Email</label>
                    <input
                      type="email"
                      value={editingBlog.email}
                      onChange={(e) => setEditingBlog({ ...editingBlog, email: e.target.value })}
                      className="w-full px-4 py-2 border rounded"
                      placeholder="Author email"
                    />
                  </div>
                  <div>
                    <label className="block mb-2 font-semibold">Social Media Links</label>
                    <div className="space-y-2">
                      <input
                        type="url"
                        value={editingBlog.twitter || ''}
                        onChange={(e) => setEditingBlog({ ...editingBlog, twitter: e.target.value })}
                        className="w-full px-4 py-2 border rounded"
                        placeholder="Twitter/X URL"
                      />
                      <input
                        type="url"
                        value={editingBlog.facebook || ''}
                        onChange={(e) => setEditingBlog({ ...editingBlog, facebook: e.target.value })}
                        className="w-full px-4 py-2 border rounded"
                        placeholder="Facebook URL"
                      />
                      <input
                        type="url"
                        value={editingBlog.instagram || ''}
                        onChange={(e) => setEditingBlog({ ...editingBlog, instagram: e.target.value })}
                        className="w-full px-4 py-2 border rounded"
                        placeholder="Instagram URL"
                      />
                      <input
                        type="url"
                        value={editingBlog.linkedin || ''}
                        onChange={(e) => setEditingBlog({ ...editingBlog, linkedin: e.target.value })}
                        className="w-full px-4 py-2 border rounded"
                        placeholder="LinkedIn URL"
                      />
                      <input
                        type="url"
                        value={editingBlog.website || ''}
                        onChange={(e) => setEditingBlog({ ...editingBlog, website: e.target.value })}
                        className="w-full px-4 py-2 border rounded"
                        placeholder="Website URL"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block mb-2 font-semibold">Content (Rich Text HTML)</label>
                    <div className="mb-2 flex space-x-2">
                      <button
                        type="button"
                        onClick={() => document.execCommand('bold', false, null)}
                        className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300"
                        title="Bold"
                      >
                        <strong>B</strong>
                      </button>
                      <button
                        type="button"
                        onClick={() => document.execCommand('italic', false, null)}
                        className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300"
                        title="Italic"
                      >
                        <em>I</em>
                      </button>
                      <button
                        type="button"
                        onClick={() => document.execCommand('underline', false, null)}
                        className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300"
                        title="Underline"
                      >
                        <u>U</u>
                      </button>
                      <button
                        type="button"
                        onClick={() => document.execCommand('formatBlock', false, '<h2>')}
                        className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300"
                        title="Heading"
                      >
                        H2
                      </button>
                      <button
                        type="button"
                        onClick={() => document.execCommand('formatBlock', false, '<p>')}
                        className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300"
                        title="Paragraph"
                      >
                        P
                      </button>
                    </div>
                    <div
                      ref={blogEditorRef}
                      contentEditable
                      suppressContentEditableWarning={true}
                      onInput={(e) => setEditingBlog({ ...editingBlog, content: e.currentTarget.innerHTML })}
                      onBlur={(e) => setEditingBlog({ ...editingBlog, content: e.currentTarget.innerHTML })}
                      className="w-full px-4 py-2 border rounded min-h-64 focus:outline-none focus:ring-2 focus:ring-[#59844F]"
                      style={{ minHeight: '300px' }}
                    />
                  </div>
                  <div className="flex space-x-3">
                    <button
                      onClick={handleUpdateBlog}
                      className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                    >
                      Save Changes
                    </button>
                    <button
                      onClick={() => setEditingBlog(null)}
                      className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}

            {!selectedBlog && !editingBlog && (
              <>
                <div className="bg-white rounded-lg shadow p-6 mb-6">
                  <h3 className="text-xl font-semibold mb-4">Pending Approval ({loading ? '...' : pendingBlogs.length})</h3>
                  {loading ? (
                    <p className="text-gray-500">Loading...</p>
                  ) : pendingBlogs.length === 0 ? (
                    <p className="text-gray-500">No pending blogs</p>
                  ) : (
                    <div className="space-y-3 max-h-96 overflow-y-auto">
                      {pendingBlogs.map((blog) => (
                        <div key={blog.id} className="border rounded p-4 flex justify-between items-start">
                          <div className="flex-1">
                            <h4 className="font-semibold">{blog.title}</h4>
                            <p className="text-sm text-gray-600">By: {blog.fullname}</p>
                            <p className="text-xs text-gray-500">{new Date(blog.created_at).toLocaleString()}</p>
                          </div>
                          <button
                            onClick={() => setSelectedBlog(blog)}
                            className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                          >
                            <FontAwesomeIcon icon={faEye} className="mr-1" />
                            View
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="bg-white rounded-lg shadow p-6">
                  <h3 className="text-xl font-semibold mb-4">All Blogs ({loading ? '...' : allBlogs.length})</h3>
                  {loading ? (
                    <p className="text-gray-500">Loading...</p>
                  ) : (
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {allBlogs.map((blog) => (
                      <div key={blog.id} className="border rounded p-4 flex justify-between items-start">
                        <div className="flex-1">
                          <h4 className="font-semibold">{blog.title}</h4>
                          <p className="text-sm text-gray-600">By: {blog.fullname}</p>
                          <p className="text-xs">
                            Status: <span className={blog.approved ? 'text-green-600' : 'text-yellow-600'}>
                              {blog.approved ? 'Approved' : 'Pending'}
                            </span>
                          </p>
                        </div>
                        <button
                          onClick={() => setSelectedBlog(blog)}
                          className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                        >
                          <FontAwesomeIcon icon={faEye} className="mr-1" />
                          View
                        </button>
                      </div>
                    ))}
                  </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {/* User Management Section */}
        {activeSection === 'users' && (
          <div>
            <h2 className="text-3xl font-bold mb-6">User Management</h2>
            
            <div className="bg-white rounded-lg shadow p-6 mb-6">
              <h3 className="text-xl font-semibold mb-4">Create New User</h3>
              <div className="grid grid-cols-2 gap-4">
                <input
                  type="text"
                  placeholder="Username"
                  value={newUser.username}
                  onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                  className="px-4 py-2 border rounded"
                />
                <input
                  type="email"
                  placeholder="Email"
                  value={newUser.email}
                  onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                  className="px-4 py-2 border rounded"
                />
                <input
                  type="password"
                  placeholder="Password"
                  value={newUser.password}
                  onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                  className="px-4 py-2 border rounded"
                />
                <input
                  type="text"
                  placeholder="First Name"
                  value={newUser.first_name}
                  onChange={(e) => setNewUser({ ...newUser, first_name: e.target.value })}
                  className="px-4 py-2 border rounded"
                />
                <input
                  type="text"
                  placeholder="Last Name"
                  value={newUser.last_name}
                  onChange={(e) => setNewUser({ ...newUser, last_name: e.target.value })}
                  className="px-4 py-2 border rounded"
                />
              </div>
              <button
                onClick={handleCreateUser}
                className="mt-4 px-6 py-2 bg-green-600 text-white rounded hover:bg-green-700"
              >
                Create User
              </button>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-xl font-semibold mb-4">All Users ({loading ? '...' : users.length})</h3>
              {loading ? (
                <p className="text-gray-500">Loading...</p>
              ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left">Username</th>
                      <th className="px-4 py-2 text-left">Email</th>
                      <th className="px-4 py-2 text-left">Name</th>
                      <th className="px-4 py-2 text-left">Staff</th>
                      <th className="px-4 py-2 text-left">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user) => (
                      <tr key={user.username} className="border-t">
                        <td className="px-4 py-2">{user.username}</td>
                        <td className="px-4 py-2">{user.email}</td>
                        <td className="px-4 py-2">{user.first_name} {user.last_name}</td>
                        <td className="px-4 py-2">
                          <button
                            onClick={() => handleToggleUserStaff(user.id, user.is_staff)}
                            className={`px-2 py-1 text-xs rounded ${
                              user.is_staff ? 'bg-green-200 text-green-800' : 'bg-gray-200 text-gray-800'
                            }`}
                          >
                            {user.is_staff ? 'Staff' : 'User'}
                          </button>
                        </td>
                        <td className="px-4 py-2">
                          <button
                            onClick={() => handleDeleteUser(user.id)}
                            className="px-2 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              )}
            </div>
          </div>
        )}

        {/* Reports & Community Section */}
        {activeSection === 'reports' && (
          <div>
            <h2 className="text-3xl font-bold mb-6">Reported Community Messages</h2>
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-xl font-semibold mb-4">Reported Messages ({loading ? '...' : reports.length})</h3>
              {loading ? (
                <p className="text-gray-500">Loading...</p>
              ) : reports.length === 0 ? (
                <p className="text-gray-500">No reported messages</p>
              ) : (
                <div className="space-y-4">
                  {reports.map((report) => (
                    <div key={report.id} className="border rounded p-4">
                      <p className="font-semibold">{report.community_name}</p>
                      <p className="text-gray-700 my-2">{report.message_content}</p>
                      <p className="text-sm text-gray-500">Posted by: {report.message_user}</p>
                      <p className="text-xs text-gray-400">Reported: {new Date(report.timestamp).toLocaleString()}</p>
                      <button
                        onClick={() => handleDeleteMessage(report.message)}
                        className="mt-2 px-4 py-2 bg-red-600 text-white text-sm rounded hover:bg-red-700"
                      >
                        Delete Message
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Community Messages Section */}
        {activeSection === 'community' && (
          <div>
            <h2 className="text-3xl font-bold mb-6">Community Message Monitoring</h2>
            
            {/* AI Message Generation Controls */}
            <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg shadow-md p-6 mb-6 border border-indigo-200">
              <h3 className="text-xl font-semibold mb-4 text-indigo-900 flex items-center">
                <FontAwesomeIcon icon={faRobot} className="mr-2" /> 
                AI Community Engagement
              </h3>
              <p className="text-gray-700 mb-4">
                Generate contextual messages that align with each community's topic. Messages are customized based on community names (anxiety, depression, stress, support, etc.).
              </p>
              <div className="flex gap-3 flex-wrap">
                <button
                  onClick={async () => {
                    try {
                      const res = await axiosInstance.post('admin/generate-ai-messages/', { 
                        count: 5, 
                        mode: 'questions_with_replies'
                      });
                      const breakdown = res.data.breakdown;
                      alert(
                        `✅ ${res.data.message}\n\n` +
                        `📊 Generated:\n` +
                        `❓ ${breakdown.questions} Questions\n` +
                        `💡 ${breakdown.answers} Contextual Replies\n\n` +
                        `🎯 Messages tailored to community topics!`
                      );
                      fetchCommunityMessages();
                    } catch (error) {
                      console.error('Error generating messages:', error);
                      alert('Failed to generate messages');
                    }
                  }}
                  className="px-6 py-2 bg-indigo-600 text-white font-semibold rounded-lg hover:bg-indigo-700 shadow-md transition"
                >
                  <FontAwesomeIcon icon={faComment} className="mr-2" />
                  5 Questions + Replies
                </button>
                <button
                  onClick={async () => {
                    try {
                      const res = await axiosInstance.post('admin/generate-ai-messages/', { 
                        count: 5, 
                        mode: 'tips'
                      });
                      const breakdown = res.data.breakdown;
                      alert(
                        `✅ ${res.data.message}\n\n` +
                        `📊 Generated:\n` +
                        `💡 ${breakdown.tips} Helpful Tips\n\n` +
                        `🎯 Tips customized for each community!`
                      );
                      fetchCommunityMessages();
                    } catch (error) {
                      console.error('Error generating messages:', error);
                      alert('Failed to generate messages');
                    }
                  }}
                  className="px-6 py-2 bg-purple-600 text-white font-semibold rounded-lg hover:bg-purple-700 shadow-md transition"
                >
                  <FontAwesomeIcon icon={faReply} className="mr-2" />
                  5 Helpful Tips
                </button>
                <button
                  onClick={async () => {
                    const count = prompt('How many messages to generate?', '20');
                    if (!count) return;
                    try {
                      const res = await axiosInstance.post('admin/generate-ai-messages/', { 
                        count: parseInt(count), 
                        mode: 'custom'
                      });
                      const breakdown = res.data.breakdown;
                      alert(
                        `✅ ${res.data.message}\n\n` +
                        `📊 Breakdown:\n` +
                        `❓ Questions: ${breakdown.questions}\n` +
                        `💡 Answers: ${breakdown.answers}\n` +
                        `💬 Tips: ${breakdown.tips}\n\n` +
                        `🎯 All contextually matched to communities!`
                      );
                      fetchCommunityMessages();
                    } catch (error) {
                      console.error('Error generating messages:', error);
                      alert('Failed to generate messages');
                    }
                  }}
                  className="px-6 py-2 bg-teal-600 text-white font-semibold rounded-lg hover:bg-teal-700 shadow-md transition"
                >
                  <FontAwesomeIcon icon={faCog} className="mr-2" />
                  Custom Amount
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-3">
                🎯 Smart AI: Messages automatically match community topics (anxiety, depression, stress, etc.). 
                Questions get contextual replies. Tips are actionable and specific. 
                150+ unique templates ensure variety!
              </p>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-xl font-semibold mb-4">All Community Messages ({loading ? '...' : communityMessages.length})</h3>
              {loading ? (
                <p className="text-gray-500">Loading...</p>
              ) : communityMessages.length === 0 ? (
                <p className="text-gray-500">No messages found</p>
              ) : (
                <div className="space-y-4 max-h-screen overflow-y-auto">
                  {communityMessages.map((message) => (
                    <div key={message.id} className="border rounded p-4 hover:bg-gray-50">
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex-1">
                          <p className="font-semibold text-lg">{message.community_name}</p>
                          <p className="text-sm text-gray-600">
                            By: {message.username} ({message.user_email})
                            {message.username === 'SereniAI_Bot' && (
                              <span className="ml-2 px-2 py-1 bg-indigo-100 text-indigo-800 text-xs rounded-full">AI Bot</span>
                            )}
                          </p>
                        </div>
                        <button
                          onClick={() => {
                            if (window.confirm('Delete this message?')) {
                              handleDeleteMessage(message.id);
                            }
                          }}
                          className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700"
                        >
                          <FontAwesomeIcon icon={faTrash} /> Delete
                        </button>
                      </div>
                      <p className="text-gray-700 my-2">{message.content}</p>
                      <p className="text-xs text-gray-400">Posted: {new Date(message.timestamp).toLocaleString()}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Activities Section */}
        {activeSection === 'activities' && (
          <div>
            <h2 className="text-3xl font-bold mb-6">Activity Lounge Management</h2>
            
            {/* Activity Form Modal */}
            {activityForm && activityType && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-screen overflow-y-auto">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-2xl font-bold">
                      {activityForm.isNew ? 'Add New' : 'Edit'} {activityType.slice(0, -1).charAt(0).toUpperCase() + activityType.slice(1, -1)}
                    </h3>
                    <button onClick={handleCancelActivityForm} className="text-gray-500 hover:text-gray-700">
                      <FontAwesomeIcon icon={faTimesCircle} size="lg" />
                    </button>
                  </div>
                  
                  {/* Movies Form */}
                  {activityType === 'movies' && (
                    <div className="space-y-4">
                      <div>
                        <label className="block mb-2 font-semibold">Title *</label>
                        <input
                          type="text"
                          value={activityForm.title || ''}
                          onChange={(e) => setActivityForm({ ...activityForm, title: e.target.value })}
                          className="w-full px-4 py-2 border rounded"
                          placeholder="Movie title"
                        />
                      </div>
                      <div>
                        <label className="block mb-2 font-semibold">Description</label>
                        <textarea
                          value={activityForm.description || ''}
                          onChange={(e) => setActivityForm({ ...activityForm, description: e.target.value })}
                          className="w-full px-4 py-2 border rounded"
                          rows="3"
                          placeholder="Movie description"
                        />
                      </div>
                      <div>
                        <label className="block mb-2 font-semibold">Image URL</label>
                        <input
                          type="url"
                          value={activityForm.image_url || ''}
                          onChange={(e) => setActivityForm({ ...activityForm, image_url: e.target.value })}
                          className="w-full px-4 py-2 border rounded"
                          placeholder="https://example.com/image.jpg"
                        />
                      </div>
                      <div>
                        <label className="block mb-2 font-semibold">External URL (Watch Link)</label>
                        <input
                          type="url"
                          value={activityForm.external_url || ''}
                          onChange={(e) => setActivityForm({ ...activityForm, external_url: e.target.value })}
                          className="w-full px-4 py-2 border rounded"
                          placeholder="https://example.com/movie"
                        />
                      </div>
                      <div>
                        <label className="block mb-2 font-semibold">Moods (comma-separated) *</label>
                        <input
                          type="text"
                          value={activityForm.moods || ''}
                          onChange={(e) => setActivityForm({ ...activityForm, moods: e.target.value })}
                          className="w-full px-4 py-2 border rounded"
                          placeholder="e.g., happy, sad, anxious"
                        />
                      </div>
                    </div>
                  )}

                  {/* Games Form */}
                  {activityType === 'games' && (
                    <div className="space-y-4">
                      <div>
                        <label className="block mb-2 font-semibold">Title *</label>
                        <input
                          type="text"
                          value={activityForm.title || ''}
                          onChange={(e) => setActivityForm({ ...activityForm, title: e.target.value })}
                          className="w-full px-4 py-2 border rounded"
                          placeholder="Game title"
                        />
                      </div>
                      <div>
                        <label className="block mb-2 font-semibold">Description</label>
                        <textarea
                          value={activityForm.description || ''}
                          onChange={(e) => setActivityForm({ ...activityForm, description: e.target.value })}
                          className="w-full px-4 py-2 border rounded"
                          rows="3"
                          placeholder="Game description"
                        />
                      </div>
                      <div>
                        <label className="block mb-2 font-semibold">Image URL</label>
                        <input
                          type="url"
                          value={activityForm.image_url || ''}
                          onChange={(e) => setActivityForm({ ...activityForm, image_url: e.target.value })}
                          className="w-full px-4 py-2 border rounded"
                          placeholder="https://example.com/image.jpg"
                        />
                      </div>
                      <div>
                        <label className="block mb-2 font-semibold">Play URL</label>
                        <input
                          type="url"
                          value={activityForm.play_url || ''}
                          onChange={(e) => setActivityForm({ ...activityForm, play_url: e.target.value })}
                          className="w-full px-4 py-2 border rounded"
                          placeholder="https://example.com/game"
                        />
                      </div>
                      <div>
                        <label className="block mb-2 font-semibold">Moods/Categories (comma-separated) *</label>
                        <input
                          type="text"
                          value={activityForm.moods || ''}
                          onChange={(e) => setActivityForm({ ...activityForm, moods: e.target.value })}
                          className="w-full px-4 py-2 border rounded"
                          placeholder="e.g., happy, sad, anxious"
                        />
                      </div>
                    </div>
                  )}

                  {/* Exercises Form */}
                  {activityType === 'exercises' && (
                    <div className="space-y-4">
                      <div>
                        <label className="block mb-2 font-semibold">Name *</label>
                        <input
                          type="text"
                          value={activityForm.name || ''}
                          onChange={(e) => setActivityForm({ ...activityForm, name: e.target.value })}
                          className="w-full px-4 py-2 border rounded"
                          placeholder="Exercise name"
                        />
                      </div>
                      <div>
                        <label className="block mb-2 font-semibold">Exercise Type</label>
                        <input
                          type="text"
                          value={activityForm.exercise_type || ''}
                          onChange={(e) => setActivityForm({ ...activityForm, exercise_type: e.target.value })}
                          className="w-full px-4 py-2 border rounded"
                          placeholder="e.g., breathing, stretching"
                        />
                      </div>
                      <div>
                        <label className="block mb-2 font-semibold">Reason</label>
                        <textarea
                          value={activityForm.reason || ''}
                          onChange={(e) => setActivityForm({ ...activityForm, reason: e.target.value })}
                          className="w-full px-4 py-2 border rounded"
                          rows="3"
                          placeholder="Why this exercise helps"
                        />
                      </div>
                      <div>
                        <label className="block mb-2 font-semibold">GIF URL</label>
                        <input
                          type="url"
                          value={activityForm.gif_url || ''}
                          onChange={(e) => setActivityForm({ ...activityForm, gif_url: e.target.value })}
                          className="w-full px-4 py-2 border rounded"
                          placeholder="https://example.com/exercise.gif"
                        />
                      </div>
                      <div>
                        <label className="block mb-2 font-semibold">Moods (comma-separated) *</label>
                        <input
                          type="text"
                          value={activityForm.moods || ''}
                          onChange={(e) => setActivityForm({ ...activityForm, moods: e.target.value })}
                          className="w-full px-4 py-2 border rounded"
                          placeholder="e.g., happy, sad, anxious"
                        />
                      </div>
                    </div>
                  )}

                  {/* Sounds Form */}
                  {activityType === 'sounds' && (
                    <div className="space-y-4">
                      <div>
                        <label className="block mb-2 font-semibold">Name *</label>
                        <input
                          type="text"
                          value={activityForm.name || ''}
                          onChange={(e) => setActivityForm({ ...activityForm, name: e.target.value })}
                          className="w-full px-4 py-2 border rounded"
                          placeholder="Sound name"
                        />
                      </div>
                      <div>
                        <label className="block mb-2 font-semibold">Category</label>
                        <input
                          type="text"
                          value={activityForm.category || ''}
                          onChange={(e) => setActivityForm({ ...activityForm, category: e.target.value })}
                          className="w-full px-4 py-2 border rounded"
                          placeholder="e.g., nature, meditation"
                        />
                      </div>
                      <div>
                        <label className="block mb-2 font-semibold">Duration</label>
                        <input
                          type="text"
                          value={activityForm.duration || ''}
                          onChange={(e) => setActivityForm({ ...activityForm, duration: e.target.value })}
                          className="w-full px-4 py-2 border rounded"
                          placeholder="e.g., 5:30"
                        />
                      </div>
                      <div>
                        <label className="block mb-2 font-semibold">Audio URL</label>
                        <input
                          type="url"
                          value={activityForm.audio_url || ''}
                          onChange={(e) => setActivityForm({ ...activityForm, audio_url: e.target.value })}
                          className="w-full px-4 py-2 border rounded"
                          placeholder="https://example.com/sound.mp3"
                        />
                      </div>
                      <div>
                        <label className="block mb-2 font-semibold">Image URL</label>
                        <input
                          type="url"
                          value={activityForm.image_url || ''}
                          onChange={(e) => setActivityForm({ ...activityForm, image_url: e.target.value })}
                          className="w-full px-4 py-2 border rounded"
                          placeholder="https://example.com/image.jpg"
                        />
                      </div>
                      <div>
                        <label className="block mb-2 font-semibold">Moods (comma-separated) *</label>
                        <input
                          type="text"
                          value={activityForm.moods || ''}
                          onChange={(e) => setActivityForm({ ...activityForm, moods: e.target.value })}
                          className="w-full px-4 py-2 border rounded"
                          placeholder="e.g., happy, sad, anxious"
                        />
                      </div>
                    </div>
                  )}

                  {/* Books Form */}
                  {activityType === 'books' && (
                    <div className="space-y-4">
                      <div>
                        <label className="block mb-2 font-semibold">Title *</label>
                        <input
                          type="text"
                          value={activityForm.title || ''}
                          onChange={(e) => setActivityForm({ ...activityForm, title: e.target.value })}
                          className="w-full px-4 py-2 border rounded"
                          placeholder="Book title"
                        />
                      </div>
                      <div>
                        <label className="block mb-2 font-semibold">Author</label>
                        <input
                          type="text"
                          value={activityForm.author || ''}
                          onChange={(e) => setActivityForm({ ...activityForm, author: e.target.value })}
                          className="w-full px-4 py-2 border rounded"
                          placeholder="Author name"
                        />
                      </div>
                      <div>
                        <label className="block mb-2 font-semibold">Description</label>
                        <textarea
                          value={activityForm.description || ''}
                          onChange={(e) => setActivityForm({ ...activityForm, description: e.target.value })}
                          className="w-full px-4 py-2 border rounded"
                          rows="3"
                          placeholder="Book description"
                        />
                      </div>
                      <div>
                        <label className="block mb-2 font-semibold">Cover Image URL</label>
                        <input
                          type="url"
                          value={activityForm.cover_image_url || ''}
                          onChange={(e) => setActivityForm({ ...activityForm, cover_image_url: e.target.value })}
                          className="w-full px-4 py-2 border rounded"
                          placeholder="https://example.com/cover.jpg"
                        />
                      </div>
                      <div>
                        <label className="block mb-2 font-semibold">Purchase/Read URL</label>
                        <input
                          type="url"
                          value={activityForm.purchase_url || ''}
                          onChange={(e) => setActivityForm({ ...activityForm, purchase_url: e.target.value })}
                          className="w-full px-4 py-2 border rounded"
                          placeholder="https://example.com/book"
                        />
                      </div>
                      <div>
                        <label className="block mb-2 font-semibold">Moods (comma-separated) *</label>
                        <input
                          type="text"
                          value={activityForm.moods || ''}
                          onChange={(e) => setActivityForm({ ...activityForm, moods: e.target.value })}
                          className="w-full px-4 py-2 border rounded"
                          placeholder="e.g., happy, sad, anxious"
                        />
                      </div>
                    </div>
                  )}

                  {/* Form Actions */}
                  <div className="flex justify-end space-x-3 mt-6">
                    <button
                      onClick={handleCancelActivityForm}
                      className="px-6 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSaveActivity}
                      className="px-6 py-2 bg-[#59844F] text-white rounded hover:bg-[#4b7443]"
                    >
                      {activityForm.isNew ? 'Create' : 'Update'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Activity Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Movies */}
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-xl font-semibold">Movies ({activityContent.movies.length})</h3>
                  <button
                    onClick={() => handleCreateActivity('movies')}
                    className="px-3 py-1 bg-[#59844F] text-white text-sm rounded hover:bg-[#4b7443]"
                  >
                    + Add
                  </button>
                </div>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {activityContent.movies.map((item) => (
                    <div key={item.id} className="flex justify-between items-center border-b pb-2">
                      <span className="flex-1 truncate">{item.title}</span>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEditActivity('movies', item)}
                          className="px-2 py-1 bg-blue-600 text-white text-xs rounded"
                        >
                          <FontAwesomeIcon icon={faEdit} />
                        </button>
                        <button
                          onClick={() => handleDeleteActivity('movies', item.id)}
                          className="px-2 py-1 bg-red-600 text-white text-xs rounded"
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Games */}
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-xl font-semibold">Games ({activityContent.games.length})</h3>
                  <button
                    onClick={() => handleCreateActivity('games')}
                    className="px-3 py-1 bg-[#59844F] text-white text-sm rounded hover:bg-[#4b7443]"
                  >
                    + Add
                  </button>
                </div>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {activityContent.games.map((item) => (
                    <div key={item.id} className="flex justify-between items-center border-b pb-2">
                      <span className="flex-1 truncate">{item.title}</span>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEditActivity('games', item)}
                          className="px-2 py-1 bg-blue-600 text-white text-xs rounded"
                        >
                          <FontAwesomeIcon icon={faEdit} />
                        </button>
                        <button
                          onClick={() => handleDeleteActivity('games', item.id)}
                          className="px-2 py-1 bg-red-600 text-white text-xs rounded"
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Exercises */}
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-xl font-semibold">Exercises ({activityContent.exercises.length})</h3>
                  <button
                    onClick={() => handleCreateActivity('exercises')}
                    className="px-3 py-1 bg-[#59844F] text-white text-sm rounded hover:bg-[#4b7443]"
                  >
                    + Add
                  </button>
                </div>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {activityContent.exercises.map((item) => (
                    <div key={item.id} className="flex justify-between items-center border-b pb-2">
                      <span className="flex-1 truncate">{item.name}</span>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEditActivity('exercises', item)}
                          className="px-2 py-1 bg-blue-600 text-white text-xs rounded"
                        >
                          <FontAwesomeIcon icon={faEdit} />
                        </button>
                        <button
                          onClick={() => handleDeleteActivity('exercises', item.id)}
                          className="px-2 py-1 bg-red-600 text-white text-xs rounded"
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Sounds */}
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-xl font-semibold">Sounds ({activityContent.sounds.length})</h3>
                  <button
                    onClick={() => handleCreateActivity('sounds')}
                    className="px-3 py-1 bg-[#59844F] text-white text-sm rounded hover:bg-[#4b7443]"
                  >
                    + Add
                  </button>
                </div>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {activityContent.sounds.map((item) => (
                    <div key={item.id} className="flex justify-between items-center border-b pb-2">
                      <span className="flex-1 truncate">{item.name}</span>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEditActivity('sounds', item)}
                          className="px-2 py-1 bg-blue-600 text-white text-xs rounded"
                        >
                          <FontAwesomeIcon icon={faEdit} />
                        </button>
                        <button
                          onClick={() => handleDeleteActivity('sounds', item.id)}
                          className="px-2 py-1 bg-red-600 text-white text-xs rounded"
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Books */}
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-xl font-semibold">Books ({activityContent.books.length})</h3>
                  <button
                    onClick={() => handleCreateActivity('books')}
                    className="px-3 py-1 bg-[#59844F] text-white text-sm rounded hover:bg-[#4b7443]"
                  >
                    + Add
                  </button>
                </div>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {activityContent.books.map((item) => (
                    <div key={item.id} className="flex justify-between items-center border-b pb-2">
                      <span className="flex-1 truncate">{item.title}</span>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEditActivity('books', item)}
                          className="px-2 py-1 bg-blue-600 text-white text-xs rounded"
                        >
                          <FontAwesomeIcon icon={faEdit} />
                        </button>
                        <button
                          onClick={() => handleDeleteActivity('books', item.id)}
                          className="px-2 py-1 bg-red-600 text-white text-xs rounded"
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* AI Testing Section */}
        {activeSection === 'ai' && (
          <div>
            <h2 className="text-3xl font-bold mb-6">AI Testing</h2>
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-xl font-semibold mb-4">Test SereniAI</h3>
              <textarea
                value={testMessage}
                onChange={(e) => setTestMessage(e.target.value)}
                className="w-full px-4 py-2 border rounded mb-4"
                rows="4"
                placeholder="Enter a test message for the AI..."
              />
              <button
                onClick={handleTestAI}
                className="px-6 py-2 bg-[#59844F] text-white rounded hover:bg-[#4b7443] mb-4"
              >
                Send to AI
              </button>
              {aiResponse && (
                <div className="bg-gray-50 rounded p-4">
                  <h4 className="font-semibold mb-2">AI Response:</h4>
                  <p>{aiResponse}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Bulk Email Section */}
        {activeSection === 'email' && (
          <div>
            <h2 className="text-3xl font-bold mb-6">Bulk Email</h2>
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-xl font-semibold mb-4">Send Email to Users</h3>
              <div className="space-y-4">
                <div>
                  <label className="block mb-2 font-semibold">Recipients</label>
                  <select
                    value={bulkEmail.recipient_type}
                    onChange={(e) => setBulkEmail({ ...bulkEmail, recipient_type: e.target.value, custom_emails: '' })}
                    className="w-full px-4 py-2 border rounded"
                  >
                    <option value="all">All Users</option>
                    <option value="staff">Staff Only</option>
                    <option value="newsletter">Newsletter Subscribers</option>
                    <option value="custom">Custom Email List</option>
                  </select>
                </div>
                
                {bulkEmail.recipient_type === 'custom' && (
                  <div>
                    <label className="block mb-2 font-semibold">Email Addresses</label>
                    <textarea
                      value={bulkEmail.custom_emails}
                      onChange={(e) => setBulkEmail({ ...bulkEmail, custom_emails: e.target.value })}
                      className="w-full px-4 py-2 border rounded"
                      rows="4"
                      placeholder="Enter email addresses separated by commas or new lines&#10;e.g.:&#10;user1@example.com&#10;user2@example.com, user3@example.com"
                    />
                    <p className="text-sm text-gray-500 mt-1">Enter email addresses separated by commas or new lines</p>
                  </div>
                )}
                
                <div>
                  <label className="block mb-2 font-semibold">Subject</label>
                  <input
                    type="text"
                    value={bulkEmail.subject}
                    onChange={(e) => setBulkEmail({ ...bulkEmail, subject: e.target.value })}
                    className="w-full px-4 py-2 border rounded"
                    placeholder="Email subject"
                  />
                </div>
                <div>
                  <label className="block mb-2 font-semibold">Message (Rich Text)</label>
                  <div className="mb-2 flex space-x-2 flex-wrap">
                    <button
                      type="button"
                      onClick={() => document.execCommand('bold', false, null)}
                      className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300 mb-1"
                      title="Bold"
                    >
                      <strong>B</strong>
                    </button>
                    <button
                      type="button"
                      onClick={() => document.execCommand('italic', false, null)}
                      className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300 mb-1"
                      title="Italic"
                    >
                      <em>I</em>
                    </button>
                    <button
                      type="button"
                      onClick={() => document.execCommand('underline', false, null)}
                      className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300 mb-1"
                      title="Underline"
                    >
                      <u>U</u>
                    </button>
                    <button
                      type="button"
                      onClick={() => document.execCommand('formatBlock', false, '<h2>')}
                      className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300 mb-1"
                      title="Heading 2"
                    >
                      H2
                    </button>
                    <button
                      type="button"
                      onClick={() => document.execCommand('formatBlock', false, '<h3>')}
                      className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300 mb-1"
                      title="Heading 3"
                    >
                      H3
                    </button>
                    <button
                      type="button"
                      onClick={() => document.execCommand('formatBlock', false, '<p>')}
                      className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300 mb-1"
                      title="Paragraph"
                    >
                      P
                    </button>
                    <button
                      type="button"
                      onClick={() => document.execCommand('insertUnorderedList', false, null)}
                      className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300 mb-1"
                      title="Bullet List"
                    >
                      • List
                    </button>
                  </div>
                  <div
                    ref={bulkEmailEditorRef}
                    contentEditable
                    suppressContentEditableWarning={true}
                    onInput={(e) => setBulkEmail({ ...bulkEmail, message: e.currentTarget.innerHTML })}
                    onBlur={(e) => setBulkEmail({ ...bulkEmail, message: e.currentTarget.innerHTML })}
                    className="w-full px-4 py-2 border rounded min-h-48 focus:outline-none focus:ring-2 focus:ring-[#59844F]"
                    style={{ minHeight: '200px' }}
                  />
                  <p className="text-sm text-gray-500 mt-1">Use the formatting buttons above to style your email message</p>
                </div>
                <button
                  onClick={handleSendBulkEmail}
                  className="px-6 py-2 bg-[#59844F] text-white rounded hover:bg-[#4b7443]"
                >
                  Send Bulk Email
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ComprehensiveAdminDashboard;
